"use client";

/**
 * Helpers + inline badge for AI-suggested changes on recap CUSTOM FIELDS /
 * custom columns (the flat `flow_metadata[key]` lane), split out of
 * `AiChangeBadge.tsx` to keep that file under the 500-line limit. Shares the
 * suggestion-block internals (`readBlocks`, `latestPending`,
 * `FLOW_METADATA_BLOB_FIELD`, `defaultFormat`) with the built-in-field badges.
 */

import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import {
  AiDiffRow,
  FLOW_METADATA_BLOB_FIELD,
  defaultFormat,
  latestPending,
  readBlocks,
} from "./AiChangeBadge";

// ---------- custom fields / custom columns (flat flow_metadata keys) ----------
//
// Custom fields (`preOrderFlowCustomFields`) and custom value columns
// (`preOrderFlowCustomColumns`) are stored FLAT on `flow_metadata[key]`
// (see the AI transport tool's `_update_row_to_v3_line`). When the AI stages
// a change to one, the dispatcher routes it through the `flowMetadata` blob
// sentinel, so the proposed value lands at `changes.flowMetadata.to[key]` —
// NOT as a top-level `changes[key]` entry the column cells read. The blob's
// `to` is the AI's FULL intended `flow_metadata`, so it also carries unchanged
// keys; the diff is real only when the proposed value differs from the live
// one.

// `false` collapses to `null` alongside `""`/`undefined`: an unchecked or
// never-set checkbox reads as a live `null`, but the AI stages a FULL intended
// `flow_metadata` blob that spells out `false` for those keys. Without this a
// `null` → `false` comparison would surface a phantom "— → Non" diff on every
// untouched checkbox riding along in the blob.
function normalizeCustomValue(value: unknown): unknown {
  return value === "" || value === undefined || value === false ? null : value;
}

/**
 * Pending AI change for a single custom field / custom column `key` stored
 * flat in `flow_metadata`. Checks a direct `changes[key]` entry first (a
 * columnPatch-overflow staging shape), then falls back to the `flowMetadata`
 * blob. The `from` is the live flow value (the blob carries no per-key `from`
 * snapshot); returns null when the proposed value equals the live one.
 */
export function getFlowCustomFieldAiChange(
  flow: PreOrderFlow,
  key: string,
): { from: unknown; to: unknown } | null {
  const suggestion = latestPending(readBlocks(flow));
  if (!suggestion || suggestion.type !== "modify") return null;

  // Exact-key direct entry (columnPatch-overflow staging shape). Read `key`
  // verbatim — custom keys are arbitrary user strings, so `getAiChangeValues`'s
  // snake_case→camelCase aliasing (meant for built-in columns) would let an
  // underscored key cross-match an unrelated standard field.
  const directChange = suggestion.changes?.[key];
  if (
    directChange &&
    typeof directChange === "object" &&
    "to" in directChange
  ) {
    // Same normalization guard as the blob path below: a semantically
    // unchanged checkbox (e.g. null/undefined → false) must not surface a
    // phantom "— → Non" diff even when staged as a direct entry.
    if (
      normalizeCustomValue(directChange.from) ===
      normalizeCustomValue(directChange.to)
    ) {
      return null;
    }
    return { from: directChange.from, to: directChange.to };
  }

  const change = suggestion.changes?.[FLOW_METADATA_BLOB_FIELD];
  if (!change || typeof change !== "object" || !("to" in change)) return null;
  const toObj =
    change.to && typeof change.to === "object" && !Array.isArray(change.to)
      ? (change.to as Record<string, unknown>)
      : null;
  if (!toObj || !Object.hasOwn(toObj, key)) return null;

  const meta = flow.flow_metadata as Record<string, unknown> | null | undefined;
  const liveFrom =
    meta && typeof meta === "object" && !Array.isArray(meta)
      ? (meta[key] ?? null)
      : null;
  const to = toObj[key] ?? null;
  if (normalizeCustomValue(liveFrom) === normalizeCustomValue(to)) return null;
  return { from: liveFrom, to };
}

/** True when the latest pending AI modify touches this custom field key. */
export function isAiPendingCustomField(
  flow: PreOrderFlow,
  key: string,
): boolean {
  return getFlowCustomFieldAiChange(flow, key) != null;
}

/**
 * Format a custom-field value for the AI diff badge: a select value maps to
 * its option label, a checkbox to yes/no, and empty to an em dash. Shared by
 * the inline cell (wide screens) and the popover fallback (narrow screens) so
 * label/empty/checkbox behavior can't silently diverge between the two.
 */
export function formatCustomFieldValue(
  def: CustomFieldDefinition,
  value: unknown,
  t: (key: string, fallback: string) => string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  if (def.type === "select") {
    const opt = def.options?.find((o) => o.value === String(value));
    return opt?.label ?? String(value);
  }
  if (def.type === "checkbox") {
    return value ? t("common.yes", "Oui") : t("common.no", "Non");
  }
  return String(value);
}

interface AiCustomFieldBadgeProps {
  flow: PreOrderFlow;
  fieldKey: string;
  /** Optional formatter (e.g. map a select value → its option label). */
  format?: (value: unknown) => string;
}

/**
 * Inline "from → new" diff badge for a per-flow custom field / custom column
 * AI proposal. Mirrors `<AiChangeBadge>` (same violet styling) but resolves
 * the change from the flat `flow_metadata` lane. Renders nothing when no
 * pending diff exists, so callers can drop it in unconditionally.
 */
export function AiCustomFieldBadge({
  flow,
  fieldKey,
  format,
}: AiCustomFieldBadgeProps) {
  const change = getFlowCustomFieldAiChange(flow, fieldKey);
  if (!change) return null;

  const fmt = format ?? defaultFormat;
  return (
    <AiDiffRow
      fromText={fmt(change.from)}
      toText={fmt(change.to)}
      testId={`ai-custom-field-badge-${fieldKey}`}
    />
  );
}
